import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { postAPI, postAPIForm } from '../../../app/api';
import Swal from 'sweetalert2';

export default function SocialMediaPosting() {
    const [activeTab, setActiveTab] = useState('create');
    const [posts, setPosts] = useState([]);
    const [postsByDate, setPostsByDate] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedDate, setExpandedDate] = useState(null);
    
    // Credentials state
    const [credentials, setCredentials] = useState([]);
    const [selectedPlatform, setSelectedPlatform] = useState('facebook');
    const [credentialForm, setCredentialForm] = useState({
        platform: 'facebook',
        api_key: '',
        api_secret: '',
        access_token: '',
        refresh_token: '',
        page_id: '',
        account_name: '',
        expires_at: ''
    });
    
    // Form state
    const [formData, setFormData] = useState({
        id: null,
        title: '',
        content: '',
        post_date: '',
        post_time: '',
        post_to_facebook: false,
        post_to_twitter: false,
        post_to_linkedin: false,
        post_to_instagram: false,
        media: null
    });
    const [mediaPreview, setMediaPreview] = useState(null);

    useEffect(() => {
        if (activeTab === 'list') {
            fetchPostsByDate();
        } else if (activeTab === 'credentials') {
            fetchCredentials();
        }
    }, [activeTab]);

    const fetchPostsByDate = async () => {
        try {
            setLoading(true);
            const response = await postAPI('social-media/getByDate', {});
            if (response?.status === 1) {
                setPostsByDate(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, media: file }));
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setMediaPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.content || !formData.post_date || !formData.post_time) {
            Swal.fire('Validation', 'Please fill in all required fields', 'warning');
            return;
        }

        if (!formData.post_to_facebook && !formData.post_to_twitter && 
            !formData.post_to_linkedin && !formData.post_to_instagram) {
            Swal.fire('Validation', 'Please select at least one platform', 'warning');
            return;
        }

        try {
            setLoading(true);
            const formDataToSend = new FormData();
            
            // Append all form fields
            formDataToSend.append('title', formData.title);
            formDataToSend.append('content', formData.content);
            formDataToSend.append('post_date', formData.post_date);
            formDataToSend.append('post_time', formData.post_time);
            formDataToSend.append('post_to_facebook', formData.post_to_facebook ? 1 : 0);
            formDataToSend.append('post_to_twitter', formData.post_to_twitter ? 1 : 0);
            formDataToSend.append('post_to_linkedin', formData.post_to_linkedin ? 1 : 0);
            formDataToSend.append('post_to_instagram', formData.post_to_instagram ? 1 : 0);
            
            // Append media file if exists
            if (formData.media) {
                formDataToSend.append('media', formData.media);
            }

            const response = await postAPIForm('social-media/create', formDataToSend);
            
            if (response?.status === 1) {
                Swal.fire('Success', response?.msg || 'Post created successfully', 'success');
                resetForm();
                setActiveTab('list');
            } else {
                Swal.fire('Error', response?.msg || 'Failed to create post', 'error');
            }
        } catch (error) {
            console.error('Create post error:', error);
            Swal.fire('Error', 'Failed to create post', 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            id: null,
            title: '',
            content: '',
            post_date: '',
            post_time: '',
            post_to_facebook: false,
            post_to_twitter: false,
            post_to_linkedin: false,
            post_to_instagram: false,
            media: null
        });
        setMediaPreview(null);
    };

    const handleDeletePost = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this post?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('social-media/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Post deleted successfully', 'success');
                    fetchPostsByDate();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete post', 'error');
            }
        }
    };

    const handlePublishPost = async (id) => {
        const result = await Swal.fire({
            title: 'Publish Post',
            text: 'Are you sure you want to publish this post to selected platforms?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Publish',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('social-media/publish', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Post published successfully', 'success');
                    fetchPostsByDate();
                } else {
                    Swal.fire('Error', response?.msg || 'Failed to publish post', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to publish post', 'error');
            }
        }
    };

    const toggleDateExpand = (date) => {
        setExpandedDate(expandedDate === date ? null : date);
    };

    const getStatusBadge = (status) => {
        const badges = {
            'scheduled': 'badge bg-warning',
            'published': 'badge bg-success',
            'failed': 'badge bg-danger'
        };
        return badges[status] || 'badge bg-secondary';
    };

    const getPlatformIcons = (post) => {
        const icons = [];
        if (post.post_to_facebook) icons.push({ name: 'Facebook', color: '#1877F2', icon: 'bi-facebook' });
        if (post.post_to_twitter) icons.push({ name: 'Twitter', color: '#1DA1F2', icon: 'bi-twitter' });
        if (post.post_to_linkedin) icons.push({ name: 'LinkedIn', color: '#0A66C2', icon: 'bi-linkedin' });
        if (post.post_to_instagram) icons.push({ name: 'Instagram', color: '#E4405F', icon: 'bi-instagram' });
        return icons;
    };

    const fetchCredentials = async () => {
        try {
            setLoading(true);
            const response = await postAPI('social-media/getPlatformCredentials', {});
            if (response?.status === 1) {
                setCredentials(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching credentials:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCredentialInputChange = (e) => {
        const { name, value } = e.target;
        setCredentialForm(prev => ({ ...prev, [name]: value }));
    };

    const handlePlatformChange = (platform) => {
        setSelectedPlatform(platform);
        const existingCred = credentials.find(c => c.platform === platform);
        if (existingCred) {
            setCredentialForm({
                platform: platform,
                api_key: existingCred.api_key || '',
                api_secret: existingCred.api_secret || '',
                access_token: existingCred.access_token || '',
                refresh_token: existingCred.refresh_token || '',
                page_id: existingCred.page_id || '',
                account_name: existingCred.account_name || '',
                expires_at: existingCred.expires_at ? existingCred.expires_at.split(' ')[0] : ''
            });
        } else {
            setCredentialForm({
                platform: platform,
                api_key: '',
                api_secret: '',
                access_token: '',
                refresh_token: '',
                page_id: '',
                account_name: '',
                expires_at: ''
            });
        }
    };

    const handleSaveCredentials = async (e) => {
        e.preventDefault();

        if (!credentialForm.access_token) {
            Swal.fire('Validation', 'Access Token is required', 'warning');
            return;
        }

        try {
            setLoading(true);
            const response = await postAPI('social-media/savePlatformCredentials', credentialForm);
            
            if (response?.status === 1) {
                Swal.fire('Success', 'Credentials saved successfully', 'success');
                fetchCredentials();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save credentials', 'error');
            }
        } catch (error) {
            console.error('Save credentials error:', error);
            Swal.fire('Error', 'Failed to save credentials', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePlatformStatus = async (platform) => {
        try {
            const response = await postAPI('social-media/togglePlatformStatus', { platform });
            if (response?.status === 1) {
                Swal.fire('Success', 'Platform status updated', 'success');
                fetchCredentials();
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    const handleDeleteCredentials = async (platform) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: `Are you sure you want to delete ${platform} credentials?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('social-media/deletePlatformCredentials', { platform });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Credentials deleted successfully', 'success');
                    fetchCredentials();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete credentials', 'error');
            }
        }
    };

    const getPlatformInfo = (platform) => {
        const info = {
            facebook: { 
                name: 'Facebook', 
                color: '#1877F2', 
                icon: 'bi-facebook',
                fields: ['access_token', 'page_id', 'account_name', 'expires_at']
            },
            twitter: { 
                name: 'Twitter (X)', 
                color: '#1DA1F2', 
                icon: 'bi-twitter',
                fields: ['access_token', 'api_key', 'api_secret', 'account_name', 'expires_at']
            },
            linkedin: { 
                name: 'LinkedIn', 
                color: '#0A66C2', 
                icon: 'bi-linkedin',
                fields: ['access_token', 'page_id', 'account_name', 'expires_at']
            },
            instagram: { 
                name: 'Instagram', 
                color: '#E4405F', 
                icon: 'bi-instagram',
                fields: ['access_token', 'page_id', 'account_name', 'expires_at']
            }
        };
        return info[platform] || {};
    };

    return (
        <Wrapper>
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-lg-12'>
                        <h3 className='mb-3'>Social Media Posting</h3>
                    </div>
                </div>

                <ul className="nav nav-tabs home-tabs mb-3" role="tablist">
                    <li className="nav-item" role="presentation">
                        <button 
                            className={`nav-link ${activeTab === 'create' ? 'active' : ''}`}
                            onClick={() => setActiveTab('create')}
                        >
                            <i className="bi bi-plus-circle me-1"></i>
                            Create Post
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button 
                            className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
                            onClick={() => setActiveTab('list')}
                        >
                            <i className="bi bi-list-ul me-1"></i>
                            Post List
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button 
                            className={`nav-link ${activeTab === 'credentials' ? 'active' : ''}`}
                            onClick={() => setActiveTab('credentials')}
                        >
                            <i className="bi bi-key me-1"></i>
                            Platform Credentials
                        </button>
                    </li>
                </ul>

                <div className="tab-content">
                    {/* Create Post Tab */}
                    {activeTab === 'create' && (
                        <div className="card">
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-8">
                                            <div className="mb-3">
                                                <label className="form-label">Title *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter post title"
                                                    required
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Content *</label>
                                                <textarea
                                                    className="form-control"
                                                    name="content"
                                                    value={formData.content}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter post content"
                                                    rows="6"
                                                    required
                                                />
                                                <small className="text-muted">
                                                    {formData.content.length} characters
                                                </small>
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Media (Image/Video)</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*,video/*"
                                                    onChange={handleFileChange}
                                                />
                                                {mediaPreview && (
                                                    <div className="mt-2">
                                                        {formData.media?.type?.startsWith('video/') ? (
                                                            <video 
                                                                src={mediaPreview} 
                                                                controls 
                                                                style={{ maxWidth: '300px', maxHeight: '200px' }}
                                                            />
                                                        ) : (
                                                            <img 
                                                                src={mediaPreview} 
                                                                alt="Preview" 
                                                                style={{ maxWidth: '300px', maxHeight: '200px' }}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Post Date *</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="post_date"
                                                        value={formData.post_date}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Post Time *</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        name="post_time"
                                                        value={formData.post_time}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-4">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <h5 className="card-title mb-3">Select Platforms *</h5>
                                                    
                                                    <div className="form-check mb-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="post_to_facebook"
                                                            id="facebook"
                                                            checked={formData.post_to_facebook}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="facebook">
                                                            <i className="bi bi-facebook me-2" style={{ color: '#1877F2' }}></i>
                                                            Facebook
                                                        </label>
                                                    </div>

                                                    <div className="form-check mb-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="post_to_twitter"
                                                            id="twitter"
                                                            checked={formData.post_to_twitter}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="twitter">
                                                            <i className="bi bi-twitter me-2" style={{ color: '#1DA1F2' }}></i>
                                                            Twitter
                                                        </label>
                                                    </div>

                                                    <div className="form-check mb-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="post_to_linkedin"
                                                            id="linkedin"
                                                            checked={formData.post_to_linkedin}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="linkedin">
                                                            <i className="bi bi-linkedin me-2" style={{ color: '#0A66C2' }}></i>
                                                            LinkedIn
                                                        </label>
                                                    </div>

                                                    <div className="form-check mb-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="post_to_instagram"
                                                            id="instagram"
                                                            checked={formData.post_to_instagram}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="instagram">
                                                            <i className="bi bi-instagram me-2" style={{ color: '#E4405F' }}></i>
                                                            Instagram
                                                        </label>
                                                    </div>

                                                    <hr />
                                                    
                                                    <div className="d-grid gap-2">
                                                        <button 
                                                            type="submit" 
                                                            className="btn btn-primary"
                                                            disabled={loading}
                                                        >
                                                            {loading ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                                    Creating...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="bi bi-send me-2"></i>
                                                                    Create Post
                                                                </>
                                                            )}
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-secondary"
                                                            onClick={resetForm}
                                                        >
                                                            Reset
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Post List Tab */}
                    {activeTab === 'list' && (
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Posts by Date</h5>
                                <button className="btn btn-sm btn-primary" onClick={fetchPostsByDate}>
                                    <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                                </button>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading posts...</p>
                                    </div>
                                ) : postsByDate.length === 0 ? (
                                    <div className="alert alert-info">
                                        <i className="bi bi-info-circle me-2"></i>
                                        No posts found. Create your first post to see it here.
                                    </div>
                                ) : (
                                    <div className="accordion" id="postsAccordion">
                                        {postsByDate.map((dateGroup, index) => (
                                            <div className="accordion-item" key={dateGroup.date}>
                                                <h2 className="accordion-header">
                                                    <button
                                                        className={`accordion-button ${expandedDate === dateGroup.date ? '' : 'collapsed'}`}
                                                        type="button"
                                                        onClick={() => toggleDateExpand(dateGroup.date)}
                                                    >
                                                        <strong>{new Date(dateGroup.date).toLocaleDateString('en-US', { 
                                                            weekday: 'long', 
                                                            year: 'numeric', 
                                                            month: 'long', 
                                                            day: 'numeric' 
                                                        })}</strong>
                                                        <span className="badge bg-primary ms-3">
                                                            {dateGroup.post_count} {dateGroup.post_count === 1 ? 'Post' : 'Posts'}
                                                        </span>
                                                    </button>
                                                </h2>
                                                <div className={`accordion-collapse collapse ${expandedDate === dateGroup.date ? 'show' : ''}`}>
                                                    <div className="accordion-body">
                                                        <div className="row">
                                                            {dateGroup.posts.map(post => (
                                                                <div className="col-md-6 mb-3" key={post.id}>
                                                                    <div className="card h-100">
                                                                        <div className="card-body">
                                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                <h5 className="card-title mb-0">{post.title}</h5>
                                                                                <span className={getStatusBadge(post.status)}>
                                                                                    {post.status}
                                                                                </span>
                                                                            </div>
                                                                            
                                                                            <p className="text-muted small mb-2">
                                                                                <i className="bi bi-clock me-1"></i>
                                                                                {post.post_time}
                                                                            </p>

                                                                            <p className="card-text">{post.content}</p>

                                                                            {post.media_url && (
                                                                                <div className="mb-2">
                                                                                    {post.media_type === 'video' ? (
                                                                                        <video 
                                                                                            src={post.media_url} 
                                                                                            controls 
                                                                                            style={{ maxWidth: '100%', maxHeight: '150px' }}
                                                                                        />
                                                                                    ) : (
                                                                                        <img 
                                                                                            src={post.media_url} 
                                                                                            alt={post.title}
                                                                                            className="img-fluid"
                                                                                            style={{ maxHeight: '150px', objectFit: 'cover' }}
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            )}

                                                                            <div className="mb-3">
                                                                                <small className="text-muted">Platforms: </small>
                                                                                {getPlatformIcons(post).map((platform, idx) => (
                                                                                    <i 
                                                                                        key={idx}
                                                                                        className={`${platform.icon} me-2`}
                                                                                        style={{ color: platform.color, fontSize: '1.2rem' }}
                                                                                        title={platform.name}
                                                                                    ></i>
                                                                                ))}
                                                                            </div>

                                                                            <div className="d-flex gap-2">
                                                                                {post.status === 'scheduled' && (
                                                                                    <button 
                                                                                        className="btn btn-sm btn-success"
                                                                                        onClick={() => handlePublishPost(post.id)}
                                                                                    >
                                                                                        <i className="bi bi-send me-1"></i>
                                                                                        Publish Now
                                                                                    </button>
                                                                                )}
                                                                                <button 
                                                                                    className="btn btn-sm btn-danger"
                                                                                    onClick={() => handleDeletePost(post.id)}
                                                                                >
                                                                                    <i className="bi bi-trash me-1"></i>
                                                                                    Delete
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Platform Credentials Tab */}
                    {activeTab === 'credentials' && (
                        <div className="row">
                            <div className="col-md-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">Platforms</h5>
                                    </div>
                                    <div className="list-group list-group-flush">
                                        {['facebook', 'twitter', 'linkedin', 'instagram'].map(platform => {
                                            const info = getPlatformInfo(platform);
                                            const cred = credentials.find(c => c.platform === platform);
                                            return (
                                                <button
                                                    key={platform}
                                                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedPlatform === platform ? 'active' : ''}`}
                                                    onClick={() => handlePlatformChange(platform)}
                                                >
                                                    <div>
                                                        <i className={`${info.icon} me-2`} style={{ color: selectedPlatform === platform ? '#fff' : info.color, fontSize: '1.3rem' }}></i>
                                                        <strong>{info.name}</strong>
                                                    </div>
                                                    {cred && (
                                                        <span className={`badge ${cred.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                            {cred.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Existing Credentials List */}
                                {credentials.length > 0 && (
                                    <div className="card mt-3">
                                        <div className="card-header">
                                            <h6 className="mb-0">Configured Platforms</h6>
                                        </div>
                                        <div className="card-body">
                                            {credentials.map(cred => {
                                                const info = getPlatformInfo(cred.platform);
                                                return (
                                                    <div key={cred.platform} className="d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                                                        <div>
                                                            <i className={`${info.icon} me-2`} style={{ color: info.color }}></i>
                                                            <strong>{info.name}</strong>
                                                            {cred.account_name && (
                                                                <small className="text-muted d-block">{cred.account_name}</small>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => handleTogglePlatformStatus(cred.platform)}
                                                                title={cred.is_active ? 'Disable' : 'Enable'}
                                                            >
                                                                <i className={`bi bi-${cred.is_active ? 'pause' : 'play'}-circle`}></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteCredentials(cred.platform)}
                                                                title="Delete"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <i className={`${getPlatformInfo(selectedPlatform).icon} me-2`} style={{ color: getPlatformInfo(selectedPlatform).color }}></i>
                                            {getPlatformInfo(selectedPlatform).name} Credentials
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleSaveCredentials}>
                                            <div className="alert alert-info">
                                                <i className="bi bi-info-circle me-2"></i>
                                                <strong>Setup Instructions:</strong>
                                                <ul className="mb-0 mt-2">
                                                    {selectedPlatform === 'facebook' && (
                                                        <>
                                                            <li>Create app at <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer">developers.facebook.com</a></li>
                                                            <li>Get Page Access Token with <code>pages_manage_posts</code> permission</li>
                                                            <li>Get Page ID from your Facebook page settings</li>
                                                        </>
                                                    )}
                                                    {selectedPlatform === 'twitter' && (
                                                        <>
                                                            <li>Create app at <a href="https://developer.twitter.com" target="_blank" rel="noopener noreferrer">developer.twitter.com</a></li>
                                                            <li>Get Bearer Token (Access Token) from API keys section</li>
                                                            <li>Optional: Add API Key and Secret for additional features</li>
                                                        </>
                                                    )}
                                                    {selectedPlatform === 'linkedin' && (
                                                        <>
                                                            <li>Create app at <a href="https://developer.linkedin.com" target="_blank" rel="noopener noreferrer">developer.linkedin.com</a></li>
                                                            <li>Get OAuth 2.0 Access Token with <code>w_member_social</code> scope</li>
                                                            <li>Page ID is your LinkedIn URN (e.g., person or company ID)</li>
                                                        </>
                                                    )}
                                                    {selectedPlatform === 'instagram' && (
                                                        <>
                                                            <li>Use Facebook Business account and Instagram Business Account</li>
                                                            <li>Get Access Token via Facebook Graph API</li>
                                                            <li>Page ID is your Instagram Business Account ID</li>
                                                            <li><strong>Note:</strong> Instagram requires images for posts</li>
                                                        </>
                                                    )}
                                                </ul>
                                            </div>

                                            {getPlatformInfo(selectedPlatform).fields?.includes('api_key') && (
                                                <div className="mb-3">
                                                    <label className="form-label">API Key</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="api_key"
                                                        value={credentialForm.api_key}
                                                        onChange={handleCredentialInputChange}
                                                        placeholder="Enter API Key"
                                                    />
                                                </div>
                                            )}

                                            {getPlatformInfo(selectedPlatform).fields?.includes('api_secret') && (
                                                <div className="mb-3">
                                                    <label className="form-label">API Secret</label>
                                                    <input
                                                        type="password"
                                                        className="form-control"
                                                        name="api_secret"
                                                        value={credentialForm.api_secret}
                                                        onChange={handleCredentialInputChange}
                                                        placeholder="Enter API Secret"
                                                    />
                                                </div>
                                            )}

                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Access Token *
                                                    <small className="text-muted ms-2">(Required)</small>
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    name="access_token"
                                                    value={credentialForm.access_token}
                                                    onChange={handleCredentialInputChange}
                                                    placeholder="Enter Access Token"
                                                    rows="3"
                                                    required
                                                />
                                            </div>

                                            {getPlatformInfo(selectedPlatform).fields?.includes('page_id') && (
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Page/Account ID
                                                        {(selectedPlatform === 'instagram' || selectedPlatform === 'facebook') && 
                                                            <small className="text-muted ms-2">(Optional for personal posts)</small>
                                                        }
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="page_id"
                                                        value={credentialForm.page_id}
                                                        onChange={handleCredentialInputChange}
                                                        placeholder="Enter Page/Account ID"
                                                    />
                                                </div>
                                            )}

                                            <div className="mb-3">
                                                <label className="form-label">Account Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="account_name"
                                                    value={credentialForm.account_name}
                                                    onChange={handleCredentialInputChange}
                                                    placeholder="Enter account name for reference"
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Token Expiration Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="expires_at"
                                                    value={credentialForm.expires_at}
                                                    onChange={handleCredentialInputChange}
                                                />
                                                <small className="text-muted">Optional: Set reminder for token renewal</small>
                                            </div>

                                            <div className="d-grid gap-2">
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-save me-2"></i>
                                                            Save Credentials
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Wrapper>
    );
}
